import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { connect } from 'react-redux';
import { get } from 'lodash';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import * as actions from '~actions/properties/property-list';
import { communicationStatus } from '~constants';
import PropertyStatusFilter from '~components/property-status-filter';
import { mergeSearchParams, extractSearchParams } from '~helpers/history';
import { getItem, setItem } from '~helpers/storage';
import generatePath from '~settings/routing';
import PropertyLists from './property-lists';
import PropertiesBulkOperationContainer from './bulk-operation/index';
import PropertiesBulkOperationConfirmContainer from './bulk-operation/confirm-modal';

const mapStateToProps = state => ({
  list: state.dashboard.propertyList.get('list')?.toJS(),
  communication: state.dashboard.propertyList.get('communication')?.toJS(),
});

const mapDispatchToProps = dispatch => ({
  initialize: () => {
    dispatch(actions.initialize());
  },

  getProperties: filters => {
    dispatch(actions.getPropertyList(filters));
  },
});

const defaultSearchParams = {
  pageNumber: 1,
  pageSize: 10,
};

const formatFilters = filters => {
  return {
    countrySlug: filters.countrySlug || null,
    tag: filters.tag || null,
    pageNumber: Number(filters.pageNumber) || defaultSearchParams.pageNumber,
    pageSize: Number(filters.pageSize) || defaultSearchParams.pageSize,
    statuses: filters.statuses || null,
    citySlug: filters.citySlug || null,
    landlordSlug: filters.landlordSlug || null,
    search: filters.search || null,
    sort: filters.sort || null,
    landlordIds: filters.landlordIds || null,
    bookingJourneys: filters.bookingJourneys || null,
    rankTypeCN: filters.rankTypeCN || null,
    rankTypeROW: filters.rankTypeROW || null,
    displaySettings: filters.displaySettings || null,
    onlyIncludeDisplaySetting: filters.onlyIncludeDisplaySetting || null,
  };
};

const PropertyList = ({ initialize, getProperties, communication, list }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [isLarge, setIsLarge] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [unpublishReason, setUnpublishReason] = useState(null);
  const [showPropertiesBulkOperation, setShowPropertiesBulkOperation] = useState(null);
  const [showConfirmUnpublishEditor, setShowConfirmUnpublishEditor] = useState(false);

  const filters = useMemo(() => {
    const searchObj = queryString.parse(location.search);
    return formatFilters(searchObj);
  }, [location.search]);

  const largeStyle = () => {
    const propertyList = document.querySelector('.property-list');
    const propertyListWidth = propertyList ? propertyList.clientWidth : 0;

    if (propertyListWidth + 32 >= 1400) {
      setIsLarge(true);
    }

    if (propertyListWidth + 32 < 1400) {
      setIsLarge(false);
    }
  };

  useEffect(() => {
    initialize();

    window.addEventListener('resize', largeStyle);
    document.addEventListener('toggleCollapseSidebarTriggers', largeStyle);

    largeStyle();

    return () => {
      window.removeEventListener('resize', largeStyle);
      document.removeEventListener('toggleCollapseSidebarTriggers', largeStyle);
    };
  }, []);

  useEffect(() => {
    if (
      ![communicationStatus.IDLE, communicationStatus.FETCHING].includes(
        get(communication, 'status'),
      )
    ) {
      message.error(t('cms.message.error'));
    }
  }, [communication]);

  const currentRole = get(getItem('PMS_CURRENT_USER_AUTH'), 'payload.currentRoleSlug');
  const getPropertiesByFilters = () => {
    const formattedFilters = formatFilters({ ...defaultSearchParams, ...filters });
    const { type, slug } = extractSearchParams(formattedFilters.search);
    if (type && slug) {
      formattedFilters[type] = slug;
    }

    setItem('cms_properties_list_filters', formattedFilters);
    getProperties(formattedFilters);
    window.scroll(0, 0);
  };

  useEffect(() => {
    getPropertiesByFilters();
  }, [filters, currentRole]);

  const handlePageChange = (page, pageSize) => {
    const newFilters = { ...filters, pageNumber: page };

    if (pageSize !== newFilters.pageSize) {
      newFilters.pageSize = pageSize;
      newFilters.pageNumber = defaultSearchParams.pageNumber;
    }

    history.push(mergeSearchParams(newFilters, defaultSearchParams));
  };

  const handleFilterChange = filterKey => {
    const newFilters = Object.assign({}, filters, filterKey, { pageNumber: 1 });
    history.push(mergeSearchParams(newFilters, defaultSearchParams));
  };

  const handlePropertyStatusChange = propertyStatusFilters => {
    const newFilters = Object.assign({}, filters, propertyStatusFilters, { pageNumber: 1 });

    history.push(mergeSearchParams(newFilters, defaultSearchParams));
  };

  const onAddNewBtn = () => {
    const url = generatePath('property.create');
    history.push(url);
  };

  const onSelectProperty = e => {
    if (e.value) {
      setSelectedProperties(currentSelectedProperties => [
        ...new Set([...currentSelectedProperties, e.key]),
      ]);
    } else {
      setSelectedProperties(currentSelectedProperties =>
        currentSelectedProperties.filter(v => v !== e.key),
      );
    }
  };

  const onBulkOperation = e => {
    if (e === 'selectAll') {
      setSelectedProperties(currentSelectedProperties => [
        ...new Set([...currentSelectedProperties, ...list.payload.map(e => e.id)]),
      ]);
    }
    if (e === 'unselectAll') {
      const currentPageIds = list.payload.map(e => e.id);
      setSelectedProperties(currentSelectedProperties =>
        currentSelectedProperties.filter(v => !currentPageIds.includes(v)),
      );
    }
    if (e === 'unpublished') {
      setShowConfirmUnpublishEditor(true);
    }
  };

  const handleClosePropertiesBulkOperationModal = () => {
    setShowPropertiesBulkOperation(null);
  };

  const handleClosePropertiesBulkOperationConfirmModal = () => {
    setShowConfirmUnpublishEditor(null);
    setUnpublishReason(null);
  };

  return (
    <div className="property-list">
      <div className="property-list__header">
        <div className="property-list__header-container">
          <PropertyStatusFilter
            t={t}
            onChange={handlePropertyStatusChange}
            total={list.total}
            filters={filters}
            search={location.search}
            addBtn={onAddNewBtn}
            bulkOperation={onBulkOperation}
            selectedAmount={selectedProperties.length}
          />
        </div>
      </div>
      <div
        className={classNames('property-list__list', {
          'property-list__list-nomal': isLarge,
        })}
      >
        <PropertyLists
          loading={get(communication, 'status') === communicationStatus.FETCHING}
          onChange={handleFilterChange}
          dataSource={
            get(communication, 'status') === communicationStatus.FETCHING
              ? Array(10).fill({})
              : list.payload
          }
          pagination={{
            showSizeChanger: true,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            defaultCurrent: defaultSearchParams.pageNumber,
            total: list.total,
            current: filters.pageNumber,
            pageSize: filters.pageSize,
          }}
          selectedList={selectedProperties}
          onSelect={onSelectProperty}
          selectable={filters.statuses === 'PUBLISHED'}
        />
      </div>
      <If condition={showPropertiesBulkOperation}>
        <PropertiesBulkOperationContainer
          activeModal
          t={t}
          selectedProperties={selectedProperties}
          bulkOperation={showPropertiesBulkOperation}
          unpublishReason={unpublishReason}
          onSuccess={() => {
            getPropertiesByFilters();
            setSelectedProperties([]);
          }}
          handleCloseModal={handleClosePropertiesBulkOperationModal}
        />
      </If>
      <If condition={showConfirmUnpublishEditor}>
        <PropertiesBulkOperationConfirmContainer
          activeModal
          t={t}
          selectedProperties={selectedProperties}
          list={list.payload}
          onSuccess={selectedReason => {
            setUnpublishReason(selectedReason);
            setShowConfirmUnpublishEditor(false);
            setShowPropertiesBulkOperation('unpublish');
          }}
          handleCloseModal={handleClosePropertiesBulkOperationConfirmModal}
        />
      </If>
    </div>
  );
};

PropertyList.propTypes = {
  list: PropTypes.shape({
    payload: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
  }),
  communication: PropTypes.shape({
    status: PropTypes.string.isRequired,
  }),
  initialize: PropTypes.func.isRequired,
  getProperties: PropTypes.func.isRequired,
};

PropertyList.defaultProps = {
  list: {
    payload: [],
    total: 0,
  },
  communication: {
    status: '',
  },
  initialize: () => {},
  getProperties: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(PropertyList);
